import apiClient from './api';
import {
  FixedAsset,
  FixedAssetCategory,
  CreateFixedAssetCategoryDto,
  CreateFixedAssetDto,
  UpdateFixedAssetDto,
  DepreciationScheduleLine,
  PostDepreciationDto,
  BatchPostDepreciationDto,
  BatchPostResult,
  PendingDepreciationItem,
  DisposeAssetDto,
  FixedAssetDisposal,
  AssetRegisterRow,
} from '../types/fixedAssets.types';

// ── Categories ────────────────────────────────────────────────────────────────
const getAllCategories = (): Promise<FixedAssetCategory[]> =>
  apiClient.get('/FixedAssetCategories').then(r => r.data);

const createCategory = (data: CreateFixedAssetCategoryDto): Promise<FixedAssetCategory> =>
  apiClient.post('/FixedAssetCategories', data).then(r => r.data);

const updateCategory = (id: number, data: CreateFixedAssetCategoryDto): Promise<FixedAssetCategory> =>
  apiClient.put(`/FixedAssetCategories/${id}`, data).then(r => r.data);

const deleteCategory = (id: number): Promise<void> =>
  apiClient.delete(`/FixedAssetCategories/${id}`).then(() => undefined);

// ── Fixed Assets ──────────────────────────────────────────────────────────────
const getAll = (): Promise<FixedAsset[]> =>
  apiClient.get('/FixedAssets').then(r => r.data);

const getById = (id: number): Promise<FixedAsset> =>
  apiClient.get(`/FixedAssets/${id}`).then(r => r.data);

const create = (data: CreateFixedAssetDto): Promise<FixedAsset> =>
  apiClient.post('/FixedAssets', data).then(r => r.data);

const update = (id: number, data: UpdateFixedAssetDto): Promise<FixedAsset> =>
  apiClient.put(`/FixedAssets/${id}`, data).then(r => r.data);

const deleteAsset = (id: number): Promise<void> =>
  apiClient.delete(`/FixedAssets/${id}`).then(() => undefined);

// ── Depreciation Schedule ─────────────────────────────────────────────────────
const getSchedule = (assetId: number): Promise<DepreciationScheduleLine[]> =>
  apiClient.get(`/FixedAssets/${assetId}/schedule`).then(r => r.data);

const postPeriod = (data: PostDepreciationDto): Promise<{ journalEntryNumber: string }> =>
  apiClient.post('/FixedAssets/post-period', data).then(r => r.data);

// ── Batch Depreciation Run ────────────────────────────────────────────────────
const getPendingDepreciation = (year: number, month: number): Promise<PendingDepreciationItem[]> =>
  apiClient.get('/FixedAssets/pending-depreciation', { params: { year, month } }).then(r => r.data);

const postBatch = (data: BatchPostDepreciationDto): Promise<BatchPostResult> =>
  apiClient.post('/FixedAssets/post-batch', data).then(r => r.data);

// ── Disposal ──────────────────────────────────────────────────────────────────
const dispose = (assetId: number, data: DisposeAssetDto): Promise<FixedAssetDisposal> =>
  apiClient.post(`/FixedAssets/${assetId}/dispose`, data).then(r => r.data);

const getDisposals = (): Promise<FixedAssetDisposal[]> =>
  apiClient.get('/FixedAssets/disposals').then(r => r.data);

// ── Reports ───────────────────────────────────────────────────────────────────
const getAssetRegister = (asOfDate?: string): Promise<AssetRegisterRow[]> =>
  apiClient.get('/FixedAssets/register', { params: { asOfDate } }).then(r => r.data);

export const fixedAssetsService = {
  // categories
  getAllCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  // assets
  getAll,
  getById,
  create,
  update,
  deleteAsset,
  // schedule
  getSchedule,
  postPeriod,
  // batch
  getPendingDepreciation,
  postBatch,
  // disposal
  dispose,
  getDisposals,
  // reports
  getAssetRegister,
};
